import type { ThreadEntry } from "../../../api/threads/liveMessages";
import { readChangeSetHead } from "../../../api/content/changeSetsApi";
import { ENTRIES_PAGE_LIMIT, readEntries, readThread } from "../../../api/threads/threadsApi";
import { parseCollectionVersionRef } from "../../../shared/references/contentReferences";
import { appPaths } from "../../../shared/routing/appPaths";

/**
 * The ontology slice a Thread's step selected, as a lens on the Data model (F95): the
 * address names the step's output entry (`?slice=<thread id>.<entry number>`), whose
 * `term_cards` value lists the slice's terms by IRI. A link stays short however large the
 * slice, and says where the slice came from.
 */
export const ONTOLOGY_SLICE_PARAMETER = "slice";

export interface SliceAddress {
  readonly threadId: string;
  readonly entry: number;
}

export interface SliceLens extends SliceAddress {
  readonly title: string;
  /** The slice's terms, by IRI. */
  readonly iris: ReadonlySet<string>;
  /** The commit of the model version the Thread runs at, where the slice was read. */
  readonly baseCommit: string | null;
  /** The change set the run proposed after the slice, with its current head: the data model as it proposes it. */
  readonly proposed: { readonly changeSetId: string; readonly head: string } | null;
}

export function formatSliceAddress(address: SliceAddress): string {
  return `${address.threadId}.${String(address.entry)}`;
}

/** The Data model at `commit`, showing only the slice the address names. */
export function slicePath(commit: string, address: SliceAddress): string {
  return `${appPaths.ontology({ version: commit })}?${new URLSearchParams({ [ONTOLOGY_SLICE_PARAMETER]: formatSliceAddress(address) }).toString()}`;
}

export function parseSliceAddress(text: string | null): SliceAddress | null {
  if (text === null) return null;
  const separator = text.lastIndexOf(".");
  const entry = Number(text.slice(separator + 1));
  return separator <= 0 || !Number.isInteger(entry) || entry < 1 ? null : { threadId: text.slice(0, separator), entry };
}

/** The IRIs of a `term_cards` value; empty for any other. */
export function termCardIris(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((card: unknown) => (typeof card === "object" && card !== null && "iri" in card && typeof card.iri === "string" ? [card.iri] : []));
}

/**
 * The slice's terms and the newest change set proposed after it, with the head it was
 * proposed at, from the entries that follow the slice. Pure.
 */
export function sliceOfEntries(address: SliceAddress, entries: readonly ThreadEntry[]): Pick<SliceLens, "iris" | "proposed"> | null {
  const slice = entries.find((entry) => entry.number === address.entry);
  const payload = slice?.payload;
  if (payload?.kind !== "step_output" || payload.output.output_kind !== "value" || payload.output.value_kind !== "term_cards") return null;
  // The newest proposal: a rerun of Propose replaces the earlier one.
  const proposal = entries.findLast(
    (entry) => entry.number > address.entry && entry.payload.kind === "step_output" && entry.payload.output.output_kind === "change_set" && entry.payload.output.collection !== "corpus",
  );
  const output = proposal?.payload.kind === "step_output" ? proposal.payload.output : null;
  return {
    iris: new Set(termCardIris(payload.output.value)),
    proposed: output?.output_kind === "change_set" ? { changeSetId: output.change_set_id, head: output.head } : null,
  };
}

/** The slice's entry and every entry after it, page by page. */
async function entriesFrom(address: SliceAddress, signal: AbortSignal): Promise<readonly ThreadEntry[]> {
  const entries: ThreadEntry[] = [];
  let after = address.entry - 1;
  for (;;) {
    const page = await readEntries(address.threadId, after, ENTRIES_PAGE_LIMIT, signal);
    entries.push(...page.entries);
    const last = page.entries.at(-1)?.number ?? page.lastEntryNumber;
    if (page.entries.length === 0 || last >= page.lastEntryNumber) return entries;
    after = last;
  }
}

export async function readSliceLens(address: SliceAddress, signal: AbortSignal): Promise<SliceLens | null> {
  const [thread, entries] = await Promise.all([readThread(address.threadId, signal), entriesFrom(address, signal)]);
  const slice = sliceOfEntries(address, entries);
  if (slice === null) return null;
  // Edits in its review move the change set's head: "As proposed" shows it as it is now.
  const proposed = slice.proposed === null ? null : { ...slice.proposed, head: await readChangeSetHead(slice.proposed.changeSetId, signal) };
  return { ...address, iris: slice.iris, proposed, title: thread.title, baseCommit: parseCollectionVersionRef(thread.bindings.content_version)?.commit ?? null };
}
